import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Check, Maximize2, Minimize2, Info, X, Search, EyeOff, Palette, Command, ListFilter, Rows3, Layers } from 'lucide-react';
import { InteriorOption } from '../../types/interiorConfig';
import { useSelections } from './SelectionsContext';
import { useTheme } from '../styles/ThemeContext';
import { ColorSwatchPopover } from './ColorSwatchPopover';
import { PatternPane } from './PatternSlideOut';
function MarqueeName({
  name,
  isDark
}: {
  name: string;
  isDark: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (containerRef.current && textRef.current) {
      const diff = textRef.current.scrollWidth - containerRef.current.clientWidth;
      setOverflow(diff > 2 ? diff + 8 : 0);
    }
  }, [name]);
  return <div ref={containerRef} className="overflow-hidden px-1" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-1470">
      <span ref={textRef} className={`block whitespace-nowrap text-[9px] font-bold text-center uppercase tracking-wider ${isDark ? 'text-white/90' : 'text-gray-800'}`} style={{
      transform: isHovered && overflow > 0 ? `translateX(-${overflow}px)` : 'translateX(0)',
      transition: isHovered && overflow > 0 ? `transform ${Math.max(1, overflow / 40)}s ease-in-out` : 'transform 0.3s ease-out'
    }} title={name} data-id="element-1471">
        {name}
      </span>
    </div>;
}
const MANAGED_ITEMS_SET = new Set(['Refrigerator', 'Range Oven', 'Dishwasher', 'Microwave', 'Countertop']);
interface OptionsGalleryProps {
  options: InteriorOption[];
  onSelect: (optionId: string) => void;
  onClose: () => void;
  category?: string;
  itemName?: string;
  docked?: boolean;
  allowNone?: boolean;
}
const getOptionPrice = (optionId: string): number => {
  const baseId = optionId.replace(/^lots-/, '').replace(/-[2-5]$/, '');
  const prices: Record<string, number> = {
    standard: 0,
    'level-1': 1500,
    'level-2': 2500,
    'level-3': 3500,
    'level-4': 4500,
    'black-electric': 2000,
    'black-gas': 2200,
    'fridge-standard': 0,
    'fridge-french-door': 1800,
    'fridge-side-by-side': 1200,
    'fridge-counter-depth': 2400,
    'fridge-smart': 3500,
    'fridge-panel-ready': 2800,
    'dw-standard': 0,
    'dw-quiet': 450,
    'dw-third-rack': 650,
    'dw-panel-ready': 850,
    'dw-smart': 1200,
    'mw-standard': 0,
    'mw-over-range': 350,
    'mw-drawer': 1200,
    'mw-built-in': 800,
    'ct-laminate': 0,
    'ct-solid-surface': 450,
    'ct-ceramic-tile': 900,
    'ct-butcher-block': 1400,
    'ct-concrete': 1900,
    'ct-recycled-glass': 2400,
    'ct-granite': 2900,
    'ct-soapstone': 3400,
    'ct-quartz': 3900,
    'ct-quartzite': 4800,
    'ct-marble': 5800,
    'ct-porcelain-slab': 6800,
    'ct-dekton': 7800,
    'ct-calacatta': 8500,
    // Flooring
    'fl-hardwood-oak': 0,
    'fl-hardwood-walnut': 1800,
    'fl-engineered-hickory': 1200,
    'fl-lvp-standard': 600,
    'fl-porcelain-tile': 2200,
    'fl-ceramic-tile': 900,
    'fl-natural-stone': 4500,
    'fl-herringbone': 3800,
    // Backsplash
    'bs-subway-white': 0,
    'bs-subway-gray': 200,
    'bs-herringbone': 800,
    'bs-hexagon': 650,
    'bs-marble-slab': 3500,
    'bs-glass-mosaic': 1200,
    'bs-zellige': 1800,
    'bs-penny-round': 950,
    'bs-natural-stone': 2800,
    'fridges-basic-28': 0,
    'fridges-standard-30': 0,
    'fridges-economy-24': 0,
    'fridges-stainless-30': 800,
    'fridges-energy-star': 950,
    'fridges-top-freezer': 1100,
    'fridges-bottom-freezer': 1300,
    'fridges-compact-plus': 1450,
    'fridges-french-33': 1800,
    'fridges-side-by-side-36': 2100,
    'fridges-counter-33': 2400,
    'fridges-glass-door': 2700,
    'fridges-column': 2950,
    'fridges-smart-pro': 3200,
    'fridges-panel-36': 3600,
    'fridges-builtin-42': 4100,
    'fridges-pro-48': 4500,
    'fridges-chef': 4900,
    'fridges-subzero-48': 5500,
    'fridges-thermador': 6200,
    'fridges-viking': 7000,
    'fridges-lacornue': 8500,
    // Floorplan options
    'fp-ext-standard': 0,
    'fp-ext-extended': 1500,
    'fp-ground-standard': 0,
    'fp-base-developed': 760,
    'fp-base-legal-suite': 517,
    'fp-base-legal-2bed': 2150,
    'fp-main-standard': 0,
    'fp-main-extended': 1200,
    'fp-main-open': 900,
    'fp-upper-3bed': 0,
    'fp-upper-4bed': 1800,
    'fp-upper-loft': 1400
  };
  return prices[optionId] ?? prices[baseId] ?? 500;
};
const getTier = (price: number): string => {
  if (price === 0) return 'Standard';
  if (price <= 500) return 'Level 1';
  if (price <= 1000) return 'Level 2';
  if (price <= 1500) return 'Level 3';
  if (price <= 2000) return 'Level 4';
  if (price <= 2500) return 'Level 5';
  if (price <= 3000) return 'Level 6';
  if (price <= 3500) return 'Level 7';
  if (price <= 4000) return 'Level 8';
  if (price <= 5000) return 'Level 9';
  if (price <= 6000) return 'Level 10';
  if (price <= 7000) return 'Level 11';
  if (price <= 8000) return 'Level 12';
  return 'Level 13';
};
const getTierColor = (tier: string, isDark: boolean) => {
  switch (tier) {
    case 'Standard':
      return isDark ? 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30' : 'text-emerald-700 bg-emerald-100 border-emerald-200';
    case 'Level 1':
      return isDark ? 'text-blue-400 bg-blue-500/20 border-blue-500/30' : 'text-blue-700 bg-blue-100 border-blue-200';
    case 'Level 2':
      return isDark ? 'text-amber-400 bg-amber-500/20 border-amber-500/30' : 'text-amber-700 bg-amber-100 border-amber-200';
    case 'Level 3':
      return isDark ? 'text-purple-400 bg-purple-500/20 border-purple-500/30' : 'text-purple-700 bg-purple-100 border-purple-200';
    case 'Level 4':
      return isDark ? 'text-rose-400 bg-rose-500/20 border-rose-500/30' : 'text-rose-700 bg-rose-100 border-rose-200';
    case 'Level 5':
      return isDark ? 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30' : 'text-cyan-700 bg-cyan-100 border-cyan-200';
    case 'Level 6':
      return isDark ? 'text-orange-400 bg-orange-500/20 border-orange-500/30' : 'text-orange-700 bg-orange-100 border-orange-200';
    case 'Level 7':
      return isDark ? 'text-lime-400 bg-lime-500/20 border-lime-500/30' : 'text-lime-700 bg-lime-100 border-lime-200';
    case 'Level 8':
      return isDark ? 'text-indigo-400 bg-indigo-500/20 border-indigo-500/30' : 'text-indigo-700 bg-indigo-100 border-indigo-200';
    case 'Level 9':
      return isDark ? 'text-pink-400 bg-pink-500/20 border-pink-500/30' : 'text-pink-700 bg-pink-100 border-pink-200';
    case 'Level 10':
      return isDark ? 'text-teal-400 bg-teal-500/20 border-teal-500/30' : 'text-teal-700 bg-teal-100 border-teal-200';
    case 'Level 11':
      return isDark ? 'text-fuchsia-400 bg-fuchsia-500/20 border-fuchsia-500/30' : 'text-fuchsia-700 bg-fuchsia-100 border-fuchsia-200';
    case 'Level 12':
      return isDark ? 'text-sky-400 bg-sky-500/20 border-sky-500/30' : 'text-sky-700 bg-sky-100 border-sky-200';
    case 'Level 13':
      return isDark ? 'text-red-400 bg-red-500/20 border-red-500/30' : 'text-red-700 bg-red-100 border-red-200';
    default:
      return isDark ? 'text-gray-400 bg-gray-500/20 border-gray-500/30' : 'text-gray-700 bg-gray-100 border-gray-200';
  }
};
const TIER_ORDER: Record<string, number> = {
  Standard: 0,
  'Level 1': 1,
  'Level 2': 2,
  'Level 3': 3,
  'Level 4': 4,
  'Level 5': 5,
  'Level 6': 6,
  'Level 7': 7,
  'Level 8': 8,
  'Level 9': 9,
  'Level 10': 10,
  'Level 11': 11,
  'Level 12': 12,
  'Level 13': 13
};
const getOptionDescription = (optionId: string): string | null => {
  const baseId = optionId.replace(/^lots-/, '').replace(/-[2-5]$/, '');
  const descriptions: Record<string, string> = {
    standard: 'Standard electric range with 4 coil burners and manual-clean oven.',
    'level-1': 'Stainless steel gas range with 5 sealed burners and self-clean.',
    'level-2': 'Professional dual-fuel range with convection oven and griddle.',
    'level-3': 'Chef series with 6 brass burners, dual convection, and warming drawer.',
    'level-4': 'Commercial-grade 48" range with 8 burners, double oven, and infrared broiler.',
    'black-electric': 'Black stainless finish, 5 smooth-top elements, steam clean.',
    'black-gas': 'Matte black gas range with 5 sealed burners and air fry mode.',
    'fridge-standard': 'Standard 30" top-mount refrigerator with manual defrost.',
    'fridge-french-door': '36" French door with dual cooling zones, ice maker, and LED lighting.',
    'fridge-side-by-side': 'Side-by-side with through-door ice and water dispenser.',
    'fridge-counter-depth': 'Counter-depth design for a built-in look, adjustable shelving.',
    'fridge-smart': 'Smart Hub with touchscreen, internal cameras, and Wi-Fi connectivity.',
    'fridge-panel-ready': 'Panel-ready for custom cabinetry integration, fully flush design.',
    'dw-standard': 'Standard 24" built-in dishwasher with 3 wash cycles.',
    'dw-quiet': 'Ultra-quiet operation at 42 dBA with stainless steel tub.',
    'dw-third-rack': 'Third rack for utensils, flexible tines, and soil sensor.',
    'dw-panel-ready': 'Panel-ready design for seamless kitchen integration.',
    'dw-smart': 'Wi-Fi enabled with remote monitoring and auto-dispense detergent.',
    'mw-standard': 'Standard 1000W countertop microwave with turntable.',
    'mw-over-range': 'Over-the-range with ventilation fan and sensor cooking.',
    'mw-drawer': 'Drawer-style microwave for ergonomic under-counter installation.',
    'mw-built-in': 'Built-in trim kit compatible, 1200W with convection mode.',
    'ct-laminate': 'Laminate surface — durable, easy to clean, budget-friendly.',
    'ct-granite': 'Natural granite — unique patterns, heat resistant, timeless appeal.',
    'ct-quartz': 'Engineered quartz — non-porous, stain resistant, consistent color.',
    'ct-marble': 'Carrara marble — elegant veining, cool surface, premium luxury.',
    'ct-butcher-block': 'Solid hardwood — warm aesthetic, ideal prep surface, refinishable.',
    // Flooring
    'fl-hardwood-oak': 'Hardwood oak — warm, natural look, requires regular maintenance.',
    'fl-hardwood-walnut': 'Walnut hardwood — rich, deep color, beautiful grain patterns.',
    'fl-engineered-hickory': 'Engineered hickory — durable, stable, and beautiful grain.',
    'fl-lvp-standard': 'LVP standard — affordable, easy to install, good durability.',
    'fl-porcelain-tile': 'Porcelain tile — water-resistant, durable, and low maintenance.',
    'fl-ceramic-tile': 'Ceramic tile — classic look, affordable, but less durable.',
    'fl-natural-stone': 'Natural stone — unique patterns, high-end look, requires sealing.',
    'fl-herringbone': 'Herringbone pattern — elegant, timeless, and sophisticated.',
    // Backsplash
    'bs-subway-white': 'Subway tile — classic white subway, timeless and versatile.',
    'bs-subway-gray': 'Subway tile — gray subway, modern and sophisticated.',
    'bs-herringbone': 'Herringbone pattern — elegant, timeless, and sophisticated.',
    'bs-hexagon': 'Hexagon pattern — modern, geometric, and stylish.',
    'bs-marble-slab': 'Marble slab — elegant veining, premium luxury, and timeless appeal.',
    'bs-glass-mosaic': 'Glass mosaic — unique patterns, modern, and eye-catching.',
    'bs-zellige': 'Zellige pattern — intricate geometric designs, traditional and beautiful.',
    'bs-penny-round': 'Penny round — classic, timeless, and elegant.',
    'bs-natural-stone': 'Natural stone — unique patterns, high-end look, requires sealing.',
    'fridges-basic-28': 'Basic 28" top-mount, manual defrost, wire shelves.',
    'fridges-standard-30': 'Standard 30" with adjustable glass shelves and crisper drawers.',
    'fridges-economy-24': 'Compact 24" apartment-size, energy efficient.',
    'fridges-stainless-30': 'Stainless steel finish, frost-free, LED interior lighting.',
    'fridges-energy-star': 'Energy Star certified, reduces energy costs by up to 15%.',
    'fridges-top-freezer': 'Top freezer with full-width deli drawer and gallon door bins.',
    'fridges-bottom-freezer': 'Bottom freezer for eye-level fridge access, pull-out drawer.',
    'fridges-compact-plus': 'Compact design with surprisingly spacious interior and ice maker.',
    'fridges-french-33': '33" French door with dual evaporators and spill-proof shelves.',
    'fridges-side-by-side-36': '36" side-by-side with external ice/water and door alarm.',
    'fridges-counter-33': 'Counter-depth 33" for a streamlined, built-in appearance.',
    'fridges-glass-door': 'Glass door display model with LED showcase lighting.',
    'fridges-column': 'Column-style single-zone for dedicated refrigeration or freezing.',
    'fridges-smart-pro': 'Smart Hub Pro with 21" touchscreen, cameras, and voice control.',
    'fridges-panel-36': '36" panel-ready for full custom cabinetry integration.',
    'fridges-builtin-42': '42" built-in with dual compressors and air purification.',
    'fridges-pro-48': '48" professional with stainless interior and magnetic door seal.',
    'fridges-chef': 'Chef Series with precise temp zones, herb garden, and wine rack.',
    'fridges-subzero-48': 'Sub-Zero 48" with NASA-inspired air purification and dual cooling.',
    'fridges-thermador': 'Thermador Freedom with full-access drawers and diamond ice maker.',
    'fridges-viking': 'Viking Professional with ProChill™ and electronic controls.',
    'fridges-lacornue': 'La Cornue handcrafted luxury — bespoke finishes, artisan quality.'
  };
  return descriptions[optionId] ?? descriptions[baseId] ?? null;
};
function FinishMultiSelect({
  options,
  selectedFinishes,
  onToggleFinish,
  isDark
}: {
  options: Array<{
    colorOptions?: Array<{
      id: string;
      name: string;
      hex: string;
    }>;
  }>;
  selectedFinishes: Set<string>;
  onToggleFinish: (finish: string) => void;
  isDark: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  // Collect all unique finishes with their hex and count of options that have them
  const finishMap = new Map<string, {
    hex: string;
    count: number;
  }>();
  options.forEach(opt => {
    if (opt.colorOptions && opt.colorOptions.length > 1) {
      opt.colorOptions.forEach(c => {
        const existing = finishMap.get(c.name);
        if (existing) {
          existing.count++;
        } else {
          finishMap.set(c.name, {
            hex: c.hex,
            count: 1
          });
        }
      });
    }
  });
  const finishes = Array.from(finishMap.entries()).sort((a, b) => b[1].count - a[1].count);
  if (finishes.length === 0) return null;
  const allSelected = selectedFinishes.size === 0;
  const selectedCount = selectedFinishes.size;
  let buttonLabel = 'All Finishes';
  if (selectedCount === 1) {
    buttonLabel = Array.from(selectedFinishes)[0];
  } else if (selectedCount > 1) {
    buttonLabel = `${selectedCount} Finishes`;
  }
  return <div ref={dropdownRef} className="relative" data-id="element-1472">
      <button onClick={e => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    }} className={`
          flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap border
          ${selectedCount > 0 ? isDark ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20' : 'bg-teal-500/10 border-teal-500/30 text-teal-700 hover:bg-teal-500/15' : isDark ? 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200 hover:text-gray-900'}
          ${isOpen ? isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-gray-200 border-gray-300 text-gray-900' : ''}
        `} data-id="element-1473">
        <Palette className="w-3 h-3" data-id="element-1474" />
        <span data-id="element-1475">{buttonLabel}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1476" />
      </button>
      <AnimatePresence data-id="element-1477">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.97
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className={`absolute top-full left-0 mt-1.5 z-50 min-w-[200px] max-h-[320px] rounded-xl border shadow-2xl backdrop-blur-xl overflow-y-auto
              ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-1478">
            <button onClick={() => onToggleFinish('All')} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}
                ${allSelected ? isDark ? 'bg-cyan-500/10' : 'bg-teal-500/5' : ''}`} data-id="element-1479">
              <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0
                ${allSelected ? isDark ? 'bg-cyan-500 border-cyan-400' : 'bg-teal-600 border-teal-500' : isDark ? 'border-white/20 bg-white/5' : 'border-gray-300 bg-white'}`} data-id="element-1480">
                {allSelected && <Check className="w-3 h-3 text-white" data-id="element-1481" />}
              </div>
              <span className={`text-xs font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1482">
                All Finishes
              </span>
              <span className={`text-[10px] ml-auto ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1483">
                {options.filter(o => o.colorOptions && o.colorOptions.length > 1).length}
              </span>
            </button>
            <div className={`mx-3 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-1484" />
            {finishes.map(([name, {
          hex,
          count
        }]) => {
          const isChecked = selectedFinishes.has(name);
          return <button key={name} onClick={() => onToggleFinish(name)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                    ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}
                    ${isChecked ? isDark ? 'bg-cyan-500/10' : 'bg-teal-500/5' : ''}`} data-id="element-1485">
                  <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0
                    ${isChecked ? isDark ? 'bg-cyan-500 border-cyan-400' : 'bg-teal-600 border-teal-500' : isDark ? 'border-white/20 bg-white/5' : 'border-gray-300 bg-white'}`} data-id="element-1486">
                    {isChecked && <Check className="w-3 h-3 text-white" data-id="element-1487" />}
                  </div>
                  <div className="w-4 h-4 rounded-full border flex-shrink-0" style={{
              backgroundColor: hex,
              borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
            }} data-id="element-1488" />
                  <span className={`text-xs font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1489">
                    {name}
                  </span>
                  <span className={`text-[10px] ml-auto ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1490">
                    {count}
                  </span>
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function FinishFilterBar({
  options,
  selectedFinishes,
  onToggleFinish,
  isDark
}: {
  options: Array<{
    colorOptions?: Array<{
      id: string;
      name: string;
      hex: string;
    }>;
  }>;
  selectedFinishes: Set<string>;
  onToggleFinish: (finish: string) => void;
  isDark: boolean;
}) {
  const [hoveredFinish, setHoveredFinish] = useState<string | null>(null);
  // Collect unique finishes across all options with 2+ colors
  const finishMap = new Map<string, {
    hex: string;
    count: number;
  }>();
  options.forEach(opt => {
    if (opt.colorOptions && opt.colorOptions.length > 1) {
      opt.colorOptions.forEach(c => {
        const existing = finishMap.get(c.name);
        if (existing) {
          existing.count++;
        } else {
          finishMap.set(c.name, {
            hex: c.hex,
            count: 1
          });
        }
      });
    }
  });
  const finishes = Array.from(finishMap.entries()).sort((a, b) => b[1].count - a[1].count);
  if (finishes.length === 0) return null;
  const isLightColor = (hex: string) => {
    const h = hex.toLowerCase();
    return h === '#f5f5f5' || h === '#ffffff' || h === '#d4c5a9' || h === '#b8b8b8' || h === '#c0c0c0';
  };
  const activeFinish = hoveredFinish || (selectedFinishes.size === 1 ? Array.from(selectedFinishes)[0] : null);
  const displayName = activeFinish ? `${activeFinish} · ${finishMap.get(activeFinish)?.count ?? 0} options` : selectedFinishes.size > 1 ? `${selectedFinishes.size} finishes selected` : 'Filter by finish';
  return <motion.div initial={{
    opacity: 0,
    y: 8,
    scale: 0.97
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: 8,
    scale: 0.97
  }} transition={{
    duration: 0.2,
    ease: [0.22, 1, 0.36, 1]
  }} className={`flex items-center gap-4 px-5 py-2.5 rounded-full backdrop-blur-xl border shadow-2xl ${isDark ? 'bg-slate-800/90 border-white/10' : 'bg-white/90 border-gray-200 shadow-lg'}`} onClick={e => e.stopPropagation()} data-id="element-1491">
      {/* Label */}
      <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-white/50' : 'text-gray-400'}`} data-id="element-1492">
        Finishes
      </span>

      {/* Divider */}
      <div className={`w-px h-5 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-1493" />

      {/* Swatches */}
      <div className="flex items-center gap-2" data-id="element-1494">
        {finishes.map(([name, {
        hex
      }], i) => {
        const isSelected = selectedFinishes.has(name);
        const light = isLightColor(hex);
        return <motion.button key={name} initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: i * 0.04,
          duration: 0.2
        }} onClick={e => {
          e.stopPropagation();
          onToggleFinish(name);
        }} onMouseEnter={() => setHoveredFinish(name)} onMouseLeave={() => setHoveredFinish(null)} className={`relative w-8 h-8 rounded-full transition-all duration-200 ${isSelected ? 'scale-110' : 'hover:scale-110'}`} style={{
          backgroundColor: hex
        }} aria-label={`Filter by ${name}`} data-id="element-1495">
              {isSelected && <div className={`absolute -inset-[3px] rounded-full border-2 ${isDark ? 'border-cyan-400' : 'border-teal-500'}`} data-id="element-1496" />}
              {light && !isSelected && <div className={`absolute inset-0 rounded-full border ${isDark ? 'border-white/20' : 'border-gray-300'}`} data-id="element-1497" />}
              {isSelected && <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} className="absolute inset-0 flex items-center justify-center" data-id="element-1498">
                  <Check className={`w-4 h-4 ${light ? 'text-gray-700' : 'text-white'}`} strokeWidth={2.5} data-id="element-1499" />
                </motion.div>}
            </motion.button>;
      })}
      </div>

      {/* Display name */}
      <AnimatePresence mode="wait" data-id="element-1500">
        <motion.span key={displayName} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.1
      }} className={`text-xs font-medium whitespace-nowrap min-w-[80px] ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-1501">
          {displayName}
        </motion.span>
      </AnimatePresence>

      {/* Clear button */}
      {selectedFinishes.size > 0 && <motion.button initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} onClick={e => {
      e.stopPropagation();
      onToggleFinish('All');
    }} className={`p-1.5 rounded-full transition-colors ${isDark ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`} title="Clear finish filter" data-id="element-1502">
          <X className="w-3.5 h-3.5" data-id="element-1503" />
        </motion.button>}
    </motion.div>;
}
type FinishFilterMode = 'dropdown' | 'inline' | 'popover';
function FinishInlineSwatches({
  options,
  selectedFinishes,
  onToggleFinish,
  isDark
}: {
  options: Array<{
    colorOptions?: Array<{
      id: string;
      name: string;
      hex: string;
    }>;
  }>;
  selectedFinishes: Set<string>;
  onToggleFinish: (finish: string) => void;
  isDark: boolean;
}) {
  const finishMap = new Map<string, {
    hex: string;
    count: number;
  }>();
  options.forEach(opt => {
    if (opt.colorOptions && opt.colorOptions.length > 1) {
      opt.colorOptions.forEach(c => {
        const existing = finishMap.get(c.name);
        if (existing) {
          existing.count++;
        } else {
          finishMap.set(c.name, {
            hex: c.hex,
            count: 1
          });
        }
      });
    }
  });
  const finishes = Array.from(finishMap.entries()).sort((a, b) => b[1].count - a[1].count);
  if (finishes.length === 0) return null;
  const isLightColor = (hex: string) => {
    const h = hex.toLowerCase();
    return h === '#f5f5f5' || h === '#ffffff' || h === '#d4c5a9' || h === '#b8b8b8' || h === '#c0c0c0';
  };
  return <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()} data-id="element-1504">
      <span className={`text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1505">
        Finishes
      </span>
      <div className={`w-px h-4 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-1506" />
      <div className="flex items-center gap-1" data-id="element-1507">
        {finishes.map(([name, {
        hex
      }]) => {
        const isSelected = selectedFinishes.has(name);
        const light = isLightColor(hex);
        return <button key={name} onClick={e => {
          e.stopPropagation();
          onToggleFinish(name);
        }} className={`relative w-5 h-5 rounded-full transition-all duration-200 ${isSelected ? 'scale-110' : 'hover:scale-110'}`} style={{
          backgroundColor: hex
        }} aria-label={`Filter by ${name}`} title={name} data-id="element-1508">
              {isSelected && <div className={`absolute -inset-[2px] rounded-full border-[1.5px] ${isDark ? 'border-cyan-400' : 'border-teal-500'}`} data-id="element-1509" />}
              {light && !isSelected && <div className={`absolute inset-0 rounded-full border ${isDark ? 'border-white/20' : 'border-gray-300'}`} data-id="element-1510" />}
              {isSelected && <div className="absolute inset-0 flex items-center justify-center" data-id="element-1511">
                  <Check className={`w-2.5 h-2.5 ${light ? 'text-gray-700' : 'text-white'}`} strokeWidth={3} data-id="element-1512" />
                </div>}
            </button>;
      })}
      </div>
      {selectedFinishes.size > 0 && <button onClick={e => {
      e.stopPropagation();
      onToggleFinish('All');
    }} className={`p-0.5 rounded-full transition-colors ${isDark ? 'text-white/30 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`} title="Clear finish filter" data-id="element-1513">
          <X className="w-3 h-3" data-id="element-1514" />
        </button>}
    </div>;
}
function FinishFilterSwitcher({
  activeMode,
  onSwitchMode,
  isDark
}: {
  activeMode: FinishFilterMode;
  onSwitchMode: (mode: FinishFilterMode) => void;
  isDark: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'F1' || e.shiftKey && e.key === '!') {
        e.preventDefault();
        onSwitchMode('dropdown');
      } else if (e.key === 'F2' || e.shiftKey && e.key === '@') {
        e.preventDefault();
        onSwitchMode('inline');
      } else if (e.key === 'F3' || e.shiftKey && e.key === '#') {
        e.preventDefault();
        onSwitchMode('popover');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSwitchMode]);
  const modes: {
    id: FinishFilterMode;
    label: string;
    icon: React.ElementType;
    desc: string;
    shortcut: string;
  }[] = [{
    id: 'dropdown',
    label: 'Dropdown',
    icon: ListFilter,
    desc: 'Dropdown menu in header',
    shortcut: 'F1'
  }, {
    id: 'inline',
    label: 'Inline Swatches',
    icon: Rows3,
    desc: 'Swatches in the header bar',
    shortcut: 'F2'
  }, {
    id: 'popover',
    label: 'Floating Bar',
    icon: Layers,
    desc: 'Popover above the grid',
    shortcut: 'F3'
  }];
  return <div className="absolute bottom-64 right-20 z-[100] flex flex-col items-end pointer-events-auto" data-id="element-1515">
      <AnimatePresence data-id="element-1516">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} className={`mb-3 w-64 rounded-2xl border shadow-2xl backdrop-blur-xl overflow-hidden ${isDark ? 'bg-slate-900/90 border-white/10' : 'bg-white/90 border-gray-200'}`} data-id="element-1517">
            <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`} data-id="element-1518">
              <div className="flex items-center gap-2 mb-1" data-id="element-1519">
                <Palette className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1520" />
                <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1521">
                  Finish Filter Mode
                </h3>
              </div>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1522">
                Press F1-F3 to switch instantly
              </p>
            </div>

            <div className="p-2 space-y-1" data-id="element-1523">
              {modes.map(mode => {
            const isActive = activeMode === mode.id;
            const Icon = mode.icon;
            return <button key={mode.id} onClick={() => {
              onSwitchMode(mode.id);
              setIsOpen(false);
            }} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left group ${isActive ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-50 text-teal-700' : isDark ? 'hover:bg-white/5 text-white/70 hover:text-white' : 'hover:bg-gray-50 text-gray-600 hover:text-gray-900'}`} data-id="element-1524">
                    <div className={`p-2 rounded-lg ${isActive ? isDark ? 'bg-cyan-500 text-white' : 'bg-teal-600 text-white' : isDark ? 'bg-white/10' : 'bg-gray-100'}`} data-id="element-1525">
                      <Icon className="w-4 h-4" data-id="element-1526" />
                    </div>
                    <div className="flex-1" data-id="element-1527">
                      <div className="flex items-center justify-between" data-id="element-1528">
                        <span className="text-sm font-medium" data-id="element-1529">
                          {mode.label}
                        </span>
                        {isActive && <Check className="w-3.5 h-3.5" data-id="element-1530" />}
                      </div>
                      <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1531">
                        {mode.desc}
                      </span>
                    </div>
                    <div className={`text-[10px] font-mono opacity-50 px-1.5 py-0.5 rounded border ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-1532">
                      {mode.shortcut}
                    </div>
                  </button>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>

      <motion.button whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} onClick={() => setIsOpen(!isOpen)} className={`flex items-center gap-2 px-3 py-2.5 rounded-full shadow-lg backdrop-blur-md border transition-all ${isOpen ? isDark ? 'bg-cyan-500 text-white border-cyan-400' : 'bg-teal-600 text-white border-teal-500' : isDark ? 'bg-slate-800/80 text-white border-white/10 hover:bg-slate-700' : 'bg-white/80 text-gray-900 border-gray-200 hover:bg-white'}`} data-id="element-1533">
        <Palette className="w-4 h-4" data-id="element-1534" />
        <span className="text-xs font-medium pr-0.5" data-id="element-1535">Finish</span>
        <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isOpen ? 'bg-black/20' : isDark ? 'bg-white/10' : 'bg-gray-100'}`} data-id="element-1536">
          {activeMode === 'dropdown' ? 'F1' : activeMode === 'inline' ? 'F2' : 'F3'}
        </div>
      </motion.button>
    </div>;
}
function TierMultiSelect({
  tiers,
  selectedTiers,
  onToggleTier,
  optionsWithTier,
  isDark
}: {
  tiers: string[];
  selectedTiers: Set<string>;
  onToggleTier: (tier: string) => void;
  optionsWithTier: Array<{
    tier: string;
  }>;
  isDark: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  const allSelected = selectedTiers.size === 0;
  const selectedCount = selectedTiers.size;
  let buttonLabel = 'All Levels';
  if (selectedCount === 1) {
    buttonLabel = Array.from(selectedTiers)[0];
  } else if (selectedCount > 1) {
    buttonLabel = `${selectedCount} Levels`;
  }
  const tierOptions = tiers.filter(t => t !== 'All');
  return <div ref={dropdownRef} className="relative" data-id="element-1537">
      <button onClick={e => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    }} className={`
          flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap border
          ${isDark ? 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200 hover:text-gray-900'}
          ${isOpen ? isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-gray-200 border-gray-300 text-gray-900' : ''}
        `} data-id="element-1538">
        <span data-id="element-1539">{buttonLabel}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1540" />
      </button>
      <AnimatePresence data-id="element-1541">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.97
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className={`absolute top-full left-0 mt-1.5 z-50 min-w-[180px] max-h-[320px] rounded-xl border shadow-2xl backdrop-blur-xl overflow-y-auto
              ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-1542">
            <button onClick={() => onToggleTier('All')} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}
                ${allSelected ? isDark ? 'bg-cyan-500/10' : 'bg-teal-500/5' : ''}`} data-id="element-1543">
              <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0
                ${allSelected ? isDark ? 'bg-cyan-500 border-cyan-400' : 'bg-teal-600 border-teal-500' : isDark ? 'border-white/20 bg-white/5' : 'border-gray-300 bg-white'}`} data-id="element-1544">
                {allSelected && <Check className="w-3 h-3 text-white" data-id="element-1545" />}
              </div>
              <span className={`text-xs font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1546">
                All Levels
              </span>
              <span className={`text-[10px] ml-auto ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1547">
                {optionsWithTier.length}
              </span>
            </button>
            <div className={`mx-3 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-1548" />
            {tierOptions.map(tier => {
          const count = optionsWithTier.filter(o => o.tier === tier).length;
          if (count === 0) return null;
          const isChecked = selectedTiers.has(tier);
          const tierColor = getTierColor(tier, isDark);
          return <button key={tier} onClick={() => onToggleTier(tier)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                    ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}
                    ${isChecked ? isDark ? 'bg-cyan-500/10' : 'bg-teal-500/5' : ''}`} data-id="element-1549">
                  <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0
                    ${isChecked ? isDark ? 'bg-cyan-500 border-cyan-400' : 'bg-teal-600 border-teal-500' : isDark ? 'border-white/20 bg-white/5' : 'border-gray-300 bg-white'}`} data-id="element-1550">
                    {isChecked && <Check className="w-3 h-3 text-white" data-id="element-1551" />}
                  </div>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${tierColor}`} data-id="element-1552">
                    {tier}
                  </span>
                  <span className={`text-[10px] ml-auto ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1553">
                    {count}
                  </span>
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function OptionsGallery({
  options,
  onSelect,
  onClose,
  category = 'Kitchen',
  itemName = 'Option',
  docked = false,
  allowNone = false
}: OptionsGalleryProps) {
  const {
    addSelection,
    selections,
    activePackage,
    allowOverrides,
    hasConfirmedOverride,
    setHasConfirmedOverride,
    isItemLockedByPackage,
    clearActivePackage,
    setSelectionPattern
  } = useSelections();
  const {
    isDark
  } = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedTiers, setSelectedTiers] = useState<Set<string>>(new Set());
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeInfoId, setActiveInfoId] = useState<string | null>(null);
  const [needsExpand, setNeedsExpand] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedColors, setSelectedColors] = useState<Record<string, string>>({});
  const [selectedFinishes, setSelectedFinishes] = useState<Set<string>>(new Set());
  const [showOverridePrompt, setShowOverridePrompt] = useState<'locked' | 'unlocked' | null>(null);
  const [pendingSelection, setPendingSelection] = useState<any | 'none' | null>(null);
  const [finishFilterMode, setFinishFilterMode] = useState<FinishFilterMode>('inline');
  const [patternPaneOpen, setPatternPaneOpen] = useState(true);
  // Format option number as 2-digit overlay badge
  const formatNumber = (n: number): string => {
    return n.toString().padStart(2, '0');
  };
  const handleSelectColor = (optionId: string, colorId: string) => {
    setSelectedColors(prev => ({
      ...prev,
      [optionId]: colorId
    }));
  };
  const currentSelection = selections.find(s => s.itemName === itemName);
  const optionsWithTier = options.map(opt => ({
    ...opt,
    price: getOptionPrice(opt.id),
    tier: getTier(getOptionPrice(opt.id))
  }));
  const sortedOptionsWithTier = [...optionsWithTier].sort((a, b) => {
    const tierDiff = (TIER_ORDER[a.tier] ?? 99) - (TIER_ORDER[b.tier] ?? 99);
    if (tierDiff !== 0) return tierDiff;
    return a.price - b.price;
  });
  const tiers = ['All', 'Standard', 'Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5', 'Level 6', 'Level 7', 'Level 8', 'Level 9', 'Level 10', 'Level 11', 'Level 12', 'Level 13'];
  const hasFinishOptions = optionsWithTier.some(o => o.colorOptions && o.colorOptions.length > 1);
  const handleToggleTier = (tier: string) => {
    if (tier === 'All') {
      setSelectedTiers(new Set());
      return;
    }
    setSelectedTiers(prev => {
      const next = new Set(prev);
      if (next.has(tier)) {
        next.delete(tier);
      } else {
        next.add(tier);
      }
      return next;
    });
  };
  const handleToggleFinish = (finish: string) => {
    if (finish === 'All') {
      setSelectedFinishes(new Set());
      return;
    }
    setSelectedFinishes(prev => {
      const next = new Set(prev);
      if (next.has(finish)) {
        next.delete(finish);
      } else {
        next.add(finish);
      }
      return next;
    });
  };
  const filteredOptions = sortedOptionsWithTier.filter(opt => {
    const tierMatch = selectedTiers.size === 0 || selectedTiers.has(opt.tier);
    const searchMatch = !searchQuery.trim() || opt.name.toLowerCase().includes(searchQuery.toLowerCase());
    const finishMatch = selectedFinishes.size === 0 || opt.colorOptions && opt.colorOptions.some(c => selectedFinishes.has(c.name));
    return tierMatch && searchMatch && finishMatch;
  });
  const checkOverflow = () => {
    if (scrollContainerRef.current) {
      const el = scrollContainerRef.current;
      if (isExpanded) {
        setNeedsExpand(el.scrollHeight > el.clientHeight + 10);
      } else {
        setNeedsExpand(el.scrollWidth > el.clientWidth + 10);
      }
    }
  };
  const applySelection = (optionOrNone: any | 'none') => {
    if (optionOrNone === 'none') {
      addSelection({
        category,
        itemName,
        optionName: 'None',
        optionId: 'none',
        price: 0,
        isPackageItem: false
      });
      onSelect('none');
    } else {
      addSelection({
        category,
        itemName,
        optionName: optionOrNone.name,
        optionId: optionOrNone.id,
        price: optionOrNone.price,
        thumbnail: optionOrNone.thumbnail,
        isPackageItem: false
      });
      onSelect(optionOrNone.id);
    }
  };
  const processSelection = (optionOrNone: any | 'none') => {
    if (isItemLockedByPackage(category, itemName)) {
      setPendingSelection(optionOrNone);
      setShowOverridePrompt('locked');
      return;
    }
    if (activePackage && allowOverrides && !hasConfirmedOverride && activePackage.itemPrices?.[itemName] !== undefined) {
      setPendingSelection(optionOrNone);
      setShowOverridePrompt('unlocked');
      return;
    }
    applySelection(optionOrNone);
  };
  const handleSelectNone = () => {
    processSelection('none');
  };
  const handleSelect = (option: (typeof optionsWithTier)[0]) => {
    processSelection(option);
  };
  useEffect(() => {
    const raf = requestAnimationFrame(() => checkOverflow());
    window.addEventListener('resize', checkOverflow);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [filteredOptions, isExpanded]);
  const EXPANDED_3_ROW_HEIGHT = 460;
  const selectedOption = currentSelection ? filteredOptions.find(o => o.id === currentSelection.optionId) : null;
  const selectedOptionHasColors = selectedOption?.colorOptions && selectedOption.colorOptions.length > 1;
  const selectedOptionHasPatterns = selectedOption?.patternOptions && selectedOption.patternOptions.length > 1;
  const handleSelectPattern = (pattern: {
    id: string;
    name: string;
    priceModifier?: number;
  }) => {
    setSelectionPattern(itemName, {
      id: pattern.id,
      name: pattern.name,
      price: pattern.priceModifier ?? 0
    });
  };
  return <div className={docked ? 'w-full pointer-events-none' : `absolute left-[260px] right-0 z-30 flex flex-col items-center justify-end pointer-events-none px-6 ${isMinimized ? 'bottom-[48px]' : 'bottom-[60px]'}`} data-id="element-1554">
      {!isMinimized && <div className="pointer-events-auto mb-3 flex flex-col items-center gap-2" data-id="element-1555">
          {/* Finish filter bar — only in popover mode, hide when individual option color popover is showing */}
          {hasFinishOptions && finishFilterMode === 'popover' && !selectedOptionHasColors && <FinishFilterBar options={optionsWithTier} selectedFinishes={selectedFinishes} onToggleFinish={handleToggleFinish} isDark={isDark} data-id="element-1556" />}
          {/* Color swatch for selected option */}
          {selectedOptionHasColors && <ColorSwatchPopover colorOptions={selectedOption!.colorOptions!} selectedColorId={selectedColors[selectedOption!.id] || null} onSelectColor={colorId => handleSelectColor(selectedOption!.id, colorId)} optionName={selectedOption!.name} data-id="element-1557" />}
        </div>}

      <AnimatePresence mode="wait" data-id="element-1558">
        {isMinimized ? <motion.button key="minimized-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} onClick={() => setIsMinimized(false)} className={`pointer-events-auto flex items-center gap-2 px-5 py-2.5 rounded-t-xl backdrop-blur-xl border border-b-0 shadow-lg transition-colors ${isDark ? 'bg-slate-900/90 border-white/10 hover:bg-slate-800/90 text-white/70 hover:text-white' : 'bg-white/90 border-gray-200 hover:bg-white text-gray-500 hover:text-gray-700'}`} data-id="element-1559">
            <ChevronUp className="w-4 h-4" data-id="element-1560" />
            <span className="text-[11px] font-bold uppercase tracking-wider" data-id="element-1561">
              {itemName}
            </span>
            <ChevronUp className="w-4 h-4" data-id="element-1562" />
          </motion.button> : <motion.div key="full-gallery" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className={`backdrop-blur-xl border shadow-2xl pointer-events-auto overflow-hidden transition-all duration-300 flex flex-col
              ${docked ? 'w-full rounded-t-2xl border-b-0' : 'w-full max-w-5xl rounded-2xl'} 
              ${isDark ? 'bg-slate-900/80 border-white/10' : 'bg-white/85 border-white/40'}`} style={{
        maxHeight: isExpanded ? `${EXPANDED_3_ROW_HEIGHT}px` : collapsedHeight ? `${collapsedHeight}px` : '190px'
      }} data-id="element-1563">
            <AnimatePresence data-id="element-1564">
              {showOverridePrompt && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className={`absolute inset-0 z-50 flex items-center justify-center p-6 backdrop-blur-md ${isDark ? 'bg-slate-900/90' : 'bg-white/90'}`} data-id="element-1565">
                  <div className={`max-w-md w-full p-6 rounded-2xl border shadow-2xl ${isDark ? 'bg-slate-800 border-white/10' : 'bg-white border-gray-200'}`} data-id="element-1566">
                    <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1567">
                      {showOverridePrompt === 'locked' ? 'Package Selection' : 'Modify Package?'}
                    </h3>
                    <p className={`text-sm mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-1568">
                      {showOverridePrompt === 'locked' ? `This option is included in your ${activePackage?.tier} package.` : `Customizing individual options will modify your ${activePackage?.tier} package.`}
                    </p>
                    <div className="flex items-center justify-end gap-3" data-id="element-1569">
                      <button onClick={() => {
                  setShowOverridePrompt(null);
                  setPendingSelection(null);
                }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'hover:bg-white/10 text-white/80' : 'hover:bg-gray-100 text-gray-700'}`} data-id="element-1570">
                        Keep Package
                      </button>
                      <button onClick={() => {
                  if (showOverridePrompt === 'locked') {
                    clearActivePackage();
                  } else {
                    setHasConfirmedOverride(true);
                    applySelection(pendingSelection);
                  }
                  setShowOverridePrompt(null);
                  setPendingSelection(null);
                }} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${showOverridePrompt === 'locked' ? isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-900 hover:bg-gray-300' : isDark ? 'bg-amber-500 text-white hover:bg-amber-400' : 'bg-amber-500 text-white hover:bg-amber-600'}`} data-id="element-1571">
                        {showOverridePrompt === 'locked' ? 'Remove Package' : 'Customize Anyway'}
                      </button>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            <div className={`flex items-center gap-3 px-4 py-2 border-b flex-shrink-0 ${isDark ? 'border-white/5 bg-white/5' : 'border-gray-200/50 bg-gray-50/50'}`} data-id="element-1572">
              <span className={`text-xs font-bold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1573">
                {itemName}
              </span>
              <TierMultiSelect tiers={tiers} selectedTiers={selectedTiers} onToggleTier={handleToggleTier} optionsWithTier={optionsWithTier} isDark={isDark} data-id="element-1574" />
              {hasFinishOptions && finishFilterMode === 'dropdown' && <FinishMultiSelect options={optionsWithTier} selectedFinishes={selectedFinishes} onToggleFinish={handleToggleFinish} isDark={isDark} data-id="element-1575" />}
              {hasFinishOptions && finishFilterMode === 'inline' && <FinishInlineSwatches options={optionsWithTier} selectedFinishes={selectedFinishes} onToggleFinish={handleToggleFinish} isDark={isDark} data-id="element-1576" />}
              <div className="relative flex-1 max-w-[280px] min-w-[120px]" data-id="element-1577">
                <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1578" />
                <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-7 pr-6 py-1 rounded-lg text-[11px] border focus:outline-none transition-colors ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-cyan-500/50 focus:bg-white/8' : 'bg-white/50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-teal-500/50'}`} data-id="element-1579" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full transition-colors ${isDark ? 'text-white/30 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`} data-id="element-1580">
                    <X className="w-3 h-3" data-id="element-1581" />
                  </button>}
              </div>
              <span className={`text-[10px] whitespace-nowrap ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1582">
                {filteredOptions.length} of {optionsWithTier.length}
              </span>

              <div className="flex-1" data-id="element-1583" />
              <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-1584">
                {(needsExpand || isExpanded) && <button onClick={() => setIsExpanded(!isExpanded)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`} title={isExpanded ? 'Collapse view' : 'Expand view'} data-id="element-1585">
                    {isExpanded ? <Minimize2 className="w-4 h-4" data-id="element-1586" /> : <Maximize2 className="w-4 h-4" data-id="element-1587" />}
                  </button>}
                <button onClick={() => setIsMinimized(true)} className={`p-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`} title="Minimize" data-id="element-1588">
                  <ChevronDown className="w-4 h-4" data-id="element-1589" />
                </button>
              </div>
            </div>

            <div className="relative flex-1 min-h-0 flex" data-id="element-1590">
              <div className="flex-1 relative min-w-0" data-id="element-1591">
                <div ref={scrollContainerRef} onClick={() => activeInfoId && setActiveInfoId(null)} className={`h-full custom-scrollbar p-4 flex gap-3 content-start ${isExpanded ? 'flex-wrap overflow-y-auto' : 'flex-nowrap overflow-hidden'}`} data-id="element-1592">
                  {allowNone && <div className="group relative flex flex-col items-center gap-2 transition-all flex-shrink-0" data-id="element-1593">
                      <button onClick={handleSelectNone} className="relative flex flex-col items-center gap-1 transition-all text-left w-full" data-id="element-1594">
                        <div className={`
                            relative rounded-xl overflow-hidden border-2 border-dashed transition-all duration-300
                            ${docked ? 'w-28 h-28' : 'w-24 h-24'}
                            ${currentSelection?.optionId === 'none' ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/20 bg-slate-800/30 group-hover:border-white/40' : 'border-gray-300 bg-gray-50 group-hover:border-gray-400'}
                          `} data-id="element-1595">
                          <div className="w-full h-full flex items-center justify-center" data-id="element-1596">
                            <EyeOff className={`w-8 h-8 ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1597" />
                          </div>
                          <div className={`absolute bottom-0 inset-x-0 py-1 overflow-hidden backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-1598">
                            <MarqueeName name="None" isDark={isDark} data-id="element-1599" />
                          </div>
                          {currentSelection?.optionId === 'none' && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1600">
                              <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-1601">
                                <Check className="w-3 h-3 text-white" data-id="element-1602" />
                              </div>
                            </div>}
                        </div>
                        <div className="flex flex-col items-center gap-0.5 w-full px-1" data-id="element-1603">
                          <span className={`
                              text-[10px] font-bold px-2 py-0.5 rounded-full
                              ${isDark ? 'text-white/30' : 'text-gray-400'}
                            `} data-id="element-1604">
                            Included
                          </span>
                        </div>
                      </button>
                    </div>}
                  {filteredOptions.map((option, index) => {
                const isSelected = currentSelection?.optionId === option.id || currentSelection?.isPackageItem && currentSelection?.optionName?.toLowerCase() === option.name.toLowerCase();
                const isIncluded = option.price === 0;
                const tierColor = getTierColor(option.tier, isDark);
                const description = option.description || getOptionDescription(option.id);
                const hasInfo = !!description;
                const isInfoActive = activeInfoId === option.id;
                const optionNumber = allowNone ? index + 2 : index + 1;
                const formattedNum = formatNumber(optionNumber);
                return <div key={option.id} className="group relative flex flex-col items-center gap-2 transition-all flex-shrink-0" data-id="element-1605">
                        <button onClick={() => handleSelect(option)} className="relative flex flex-col items-center gap-1 transition-all text-left w-full" data-id="element-1606">
                          <div className={`
                            relative rounded-xl overflow-hidden border-2 transition-all duration-300
                            ${docked ? 'w-28 h-28' : 'w-24 h-24'}
                            ${isSelected ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/10 group-hover:border-white/30' : 'border-gray-200 group-hover:border-gray-300'}
                          `} data-id="element-1607">
                            {option.thumbnail ? <img src={option.thumbnail} alt={option.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-1608" /> : <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`} data-id="element-1609">
                                <span className={`text-[10px] ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-1610">
                                  No Image
                                </span>
                              </div>}

                            {/* Option number overlay badge */}
                            <div className={`absolute top-1.5 left-1.5 z-10 min-w-[20px] h-5 flex items-center justify-center rounded-md px-1 backdrop-blur-md text-[10px] font-bold font-mono tabular-nums ${isDark ? 'bg-black/60 text-white/80 border border-white/20' : 'bg-white/80 text-gray-700 border border-gray-300/50'}`} data-id="element-1611">
                              {formattedNum}
                            </div>

                            <div className={`absolute bottom-0 inset-x-0 py-1 overflow-hidden backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-1612">
                              <MarqueeName name={option.name} isDark={isDark} data-id="element-1613" />
                            </div>
                            {isSelected && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1614">
                                <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-1615">
                                  <Check className="w-3 h-3 text-white" data-id="element-1616" />
                                </div>
                              </div>}
                          </div>

                          {/* Price display */}
                          <div className="flex flex-col items-center gap-0.5 w-full px-1" data-id="element-1617">
                            <span className={`
                                text-[10px] font-bold px-2 py-0.5 rounded-full
                                ${isIncluded ? isDark ? 'text-white/30' : 'text-gray-400' : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'}
                              `} data-id="element-1618">
                              {isIncluded ? 'Included' : `+${option.price.toLocaleString()}`}
                            </span>
                          </div>
                        </button>

                        {hasInfo && <button onClick={e => {
                    e.stopPropagation();
                    setActiveInfoId(isInfoActive ? null : option.id);
                  }} className={`absolute top-1.5 left-1.5 z-10 p-1 rounded-full backdrop-blur-md border transition-all ${isInfoActive ? isDark ? 'bg-cyan-500 border-cyan-400 text-white shadow-lg' : 'bg-teal-500 border-teal-400 text-white shadow-lg' : isDark ? 'bg-slate-900/70 border-white/10 text-white/50 hover:text-white hover:bg-slate-900/90 opacity-0 group-hover:opacity-100' : 'bg-white/70 border-gray-200 text-gray-400 hover:text-gray-700 hover:bg-white/90 opacity-0 group-hover:opacity-100'}`} style={{
                    transition: 'opacity 0.15s, background 0.15s, color 0.15s'
                  }} data-id="element-1619">
                            <Info className="w-3 h-3" data-id="element-1620" />
                          </button>}

                        <AnimatePresence data-id="element-1621">
                          {isInfoActive && <motion.div initial={{
                      opacity: 0,
                      y: 5,
                      scale: 0.95
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} exit={{
                      opacity: 0,
                      y: 5,
                      scale: 0.95
                    }} transition={{
                      duration: 0.15
                    }} className={`absolute z-30 w-56 rounded-xl border shadow-2xl backdrop-blur-xl p-3 ${isExpanded ? 'bottom-full mb-2' : 'top-0 -translate-y-full -mt-1'} left-1/2 -translate-x-1/2 ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-1622">
                              <button onClick={() => setActiveInfoId(null)} className={`absolute top-2 right-2 p-0.5 rounded-full transition-colors ${isDark ? 'text-white/30 hover:text-white' : 'text-gray-300 hover:text-gray-600'}`} data-id="element-1623">
                                <X className="w-3 h-3" data-id="element-1624" />
                              </button>
                              <div className="space-y-2" data-id="element-1625">
                                <div data-id="element-1626">
                                  <h4 className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1627">
                                    {option.name}
                                  </h4>
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border inline-block mt-1 ${tierColor}`} data-id="element-1628">
                                    {option.tier}
                                  </span>
                                </div>
                                <p className={`text-[11px] leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-600'}`} data-id="element-1629">
                                  {description}
                                </p>
                                <div className={`flex items-center justify-between pt-1 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-1630">
                                  <span className={`text-[10px] font-bold ${isIncluded ? isDark ? 'text-emerald-400' : 'text-emerald-600' : isDark ? 'text-white/80' : 'text-gray-900'}`} data-id="element-1631">
                                    {isIncluded ? 'Included in base' : `+$${option.price.toLocaleString()}`}
                                  </span>
                                  {isSelected && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-500/15 text-teal-600'}`} data-id="element-1632">
                                      Selected
                                    </span>}
                                </div>
                              </div>
                              <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 border ${isExpanded ? `bottom-0 translate-y-1/2 border-t-0 border-l-0 ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}` : `top-full -translate-y-1/2 border-b-0 border-r-0 ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`}`} data-id="element-1633" />
                            </motion.div>}
                        </AnimatePresence>
                      </div>;
              })}
                </div>
              </div>

              {(needsExpand || isExpanded) && <button onClick={() => setIsExpanded(!isExpanded)} className={`flex-shrink-0 w-12 flex flex-col items-center justify-center gap-1.5 border-l transition-all group ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-200/50 hover:bg-gray-50'}`} title={isExpanded ? 'Collapse to single row' : 'Expand to gallery view'} data-id="element-1634">
                  <ChevronUp className={`w-4 h-4 transition-colors ${isExpanded ? isDark ? 'text-cyan-400' : 'text-teal-600' : isDark ? 'text-white/30 group-hover:text-white/60' : 'text-gray-300 group-hover:text-gray-500'}`} data-id="element-1635" />
                  <div className={`flex flex-col gap-1 ${isDark ? 'text-white/20 group-hover:text-white/40' : 'text-gray-300 group-hover:text-gray-400'} transition-colors`} data-id="element-1636">
                    <div className={`w-1.5 h-1.5 rounded-full ${isExpanded ? isDark ? 'bg-cyan-400' : 'bg-teal-500' : 'bg-current'}`} data-id="element-1637" />
                    <div className={`w-1.5 h-1.5 rounded-full ${isExpanded ? isDark ? 'bg-cyan-400/60' : 'bg-teal-500/60' : 'bg-current'}`} data-id="element-1638" />
                    <div className={`w-1.5 h-1.5 rounded-full ${isExpanded ? isDark ? 'bg-cyan-400/30' : 'bg-teal-500/30' : 'bg-current'}`} data-id="element-1639" />
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-colors ${!isExpanded ? isDark ? 'text-cyan-400' : 'text-teal-600' : isDark ? 'text-white/30 group-hover:text-white/60' : 'text-gray-300 group-hover:text-gray-500'}`} data-id="element-1640" />
                </button>}
            </div>
          </motion.div>}
      </AnimatePresence>
      {hasFinishOptions && <FinishFilterSwitcher activeMode={finishFilterMode} onSwitchMode={setFinishFilterMode} isDark={isDark} data-id="element-1641" />}

      {/* Pattern: right-side slide-in pane */}
      <AnimatePresence data-id="element-1642">
        {selectedOption && selectedOptionHasPatterns && patternPaneOpen && <PatternPane key={`pat-pane-${selectedOption.id}`} itemName={itemName} optionName={selectedOption.name} patternOptions={selectedOption.patternOptions!} selectedPatternId={currentSelection?.patternId ?? null} onSelectPattern={p => handleSelectPattern(p)} onClose={() => setPatternPaneOpen(false)} data-id="element-1643" />}
      </AnimatePresence>
    </div>;
}